// Router
import { replace } from "react-router-redux";
// Moment
import moment from "moment";
// Redux Store
import store from "../config/store";
import { BASE_URL, FALLBACK_LNG } from "../config/constants";
// API
import { get } from "./api";
// Ducks
import { hideSpinner, showSpinner } from "./spinner.ducks";
import { resetState } from "../config/combinedReducers";
// i18n
import i18n from "../config/translation/i18n";
import { setErrorNotification } from "../components/SmartNotification/SmartNotification.ducks";

const ADD_AUTH_ERROR = `ADD_AUTH_ERROR`;
const CHANGE_PASSWORD_KEY = `CHANGE_PASSWORD_KEY`;
const CHANGE_PROFILE_KEY = `CHANGE_PROFILE_KEY`;
const DESTROY_TOKENS = `DESTROY_TOKENS`;
const PROFILE_ACTIVE = `PROFILE_ACTIVE`;
const OPEN_PAYMENT = "OPEN_PAYMENT";
const REFRESH_TOKEN_KEY = `REFRESH_TOKEN_KEY`;
const REMOVE_AUTH_ERROR = `REMOVE_AUTH_ERROR`;
const RESTORE_TOKENS = `RESTORE_TOKENS`;
const RESTORE_CLAIMS = `RESTORE_CLAIMS`;
export const ROLE_KEY = `ROLE_KEY`;
const SET_PASSWORD_INTENDED_TO_CHANGE = `SET_PASSWORD_INTENDED_TO_CHANGE`;
const SET_PROFILE = `SET_PROFILE`;
const SET_PROFILE_ACTIVE_STATE = `SET_PROFILE_ACTIVE_STATE`;
const SET_PROFILE_INTENDED_TO_CHANGE = `SET_PROFILE_INTENDED_TO_CHANGE`;
const SET_TOKENS = `SET_TOKENS`;
const SET_CLAIMS = `SET_CLAIMS`;
const SET_OPEN_PAYMENT = "SET_OPEN_PAYMENT";
const TOKEN_KEY = `TOKEN_KEY`;
const CLAIMS = `CLAIMS`;
export const USERNAME_KEY = `USERNAME_KEY`;

const defaultState = {
  baseURL: process.env.PUBLIC_URL
    ? // If we are using production root folder
      process.env.PUBLIC_URL === `.`
      ? // We use root folder api
        `/api`
      : // Othwerwise we use register folder api
        `/register/api`
    : process.env.REACT_APP_API_URL || `http://localhost:51843`,
  claims: [],
  error: ``,
  profile: {
    firstName: undefined,
    isPasswordIntendedToChange: false,
    isProfileIntendedToApprove: false,
    isOpenPayment: false,
    isUserActive: undefined
  },
  refreshToken: ``,
  role: ``,
  token: ``
};

export default (state = defaultState, action) => {
  switch (action.type) {
    case ADD_AUTH_ERROR:
      return { ...state, error: action.payload };
    case DESTROY_TOKENS:
      return defaultState;
    case REMOVE_AUTH_ERROR:
      return { ...state, error: defaultState.error };
    case SET_PASSWORD_INTENDED_TO_CHANGE:
      return {
        ...state,
        profile: { ...state.profile, isPasswordIntendedToChange: action.payload }
      };
    case SET_PROFILE_INTENDED_TO_CHANGE:
      return {
        ...state,
        profile: { ...state.profile, isProfileIntendedToApprove: action.payload }
      };
    case SET_PROFILE_ACTIVE_STATE:
      return {
        ...state,
        profile: { ...state.profile, isUserActive: action.payload }
      };
    case SET_OPEN_PAYMENT:
      return {
        ...state,
        profile: { ...state.profile, isOpenPayment: action.payload }
      };
    case SET_PROFILE:
      return { ...state, profile: { ...state.profile, ...action.payload } };
    case RESTORE_CLAIMS:
      return { ...state, claims: action.payload };
    case SET_CLAIMS:
      localStorage.setItem(CLAIMS, JSON.stringify(action.payload));

      return { ...state, claims: action.payload };
    case RESTORE_TOKENS:
      return {
        ...state,
        role: action.payload.role,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        profile: {
          ...state.profile,
          isPasswordIntendedToChange: action.payload.isPasswordIntendedToChange,
          isProfileIntendedToApprove: action.payload.isProfileIntendedToApprove,
          isUserActive: action.payload.isUserActive
        }
      };
    case SET_TOKENS: {
      const newState = {
        ...state,
        refreshToken: action.payload.refreshToken || state.refreshToken,
        token: action.payload.token,
        role: action.payload.role || state.role
      };

      localStorage.setItem(REFRESH_TOKEN_KEY, newState.refreshToken);
      localStorage.setItem(ROLE_KEY, newState.role);
      localStorage.setItem(TOKEN_KEY, newState.token);
      if (action.payload.username) {
        localStorage.setItem(USERNAME_KEY, action.payload.username);
      }

      return newState;
    }
    default:
      return state;
  }
};

const destroyToken = () => {
  let preferredLanguage;
  const i18nextLng = localStorage.getItem(`i18nextLng`);
  const { language } = i18n;
  const {
    localization: { locales }
  } = store.getState();

  const isLocaleAvailable = (locale) => locales.some((local) => local.locale === locale);

  /**
   * To set up the correct language in the localStorage
   * 1. Check if the localStorage item and the i18n language are the same
   * 2. In case the localStorage item is in the available languages
   * 3. In case the localStorage is something different than what is in the available languages. Check if the i18n language is in the available languages
   * 4. If the localStorage and the i18n language fail, set the fallback language
   */
  if (i18nextLng === language) {
    preferredLanguage = i18nextLng;
  } else if (isLocaleAvailable(i18nextLng)) {
    preferredLanguage = i18nextLng;
  } else if (isLocaleAvailable(language)) {
    preferredLanguage = language;
  } else {
    preferredLanguage = FALLBACK_LNG;
  }

  /**
   * Clear all the items on localStorage but keep the user preferred language
   * and the the i18n language
   */

  localStorage.clear();
  localStorage.setItem(`i18nextLng`, preferredLanguage);
  i18n.changeLanguage(preferredLanguage);

  return {
    type: DESTROY_TOKENS
  };
};

export const logout = () => (dispatch) => {
  dispatch(destroyToken());
  dispatch(replace(`${BASE_URL}/`));
  dispatch(resetState());
};

export const restoreClaims = () => {
  const claims = JSON.parse(localStorage.getItem(CLAIMS));

  return {
    type: RESTORE_CLAIMS,
    payload: claims || []
  };
};

export const restoreToken = () => {
  const changePassword = JSON.parse(localStorage.getItem(CHANGE_PASSWORD_KEY));
  const changeProfile = JSON.parse(localStorage.getItem(CHANGE_PROFILE_KEY));
  const isUserActive = JSON.parse(localStorage.getItem(PROFILE_ACTIVE));
  const isOpenPayment = JSON.parse(localStorage.getItem(OPEN_PAYMENT));
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const role = localStorage.getItem(ROLE_KEY);
  const token = localStorage.getItem(TOKEN_KEY);

  return {
    type: RESTORE_TOKENS,
    payload: {
      isUserActive,
      isPasswordIntendedToChange: changePassword,
      isProfileIntendedToApprove: changeProfile,
      isOpenPayment,
      refreshToken: refreshToken || ``,
      role: role || ``,
      token: token || ``
    }
  };
};

export const setIsPasswordIntendedToChange = (value) => {
  localStorage.setItem(CHANGE_PASSWORD_KEY, value);
  return {
    type: SET_PASSWORD_INTENDED_TO_CHANGE,
    payload: value
  };
};

export const setIsProfileIntendedToApprove = (value) => {
  localStorage.setItem(CHANGE_PROFILE_KEY, value);
  return {
    type: SET_PROFILE_INTENDED_TO_CHANGE,
    payload: value
  };
};

export const setIsProfileActive = (value) => {
  localStorage.setItem(PROFILE_ACTIVE, value);
  return {
    type: SET_PROFILE_ACTIVE_STATE,
    payload: value
  };
};

export const setIsOpenPayment = (value) => {
  localStorage.setItem(OPEN_PAYMENT, value);
  return {
    type: SET_OPEN_PAYMENT,
    payload: value
  };
};

export const setProfile = (profile) => {
  // We get the locale language from the local storage
  // because of a race condition that sometimes does
  // not set locale properly
  moment.locale(localStorage.getItem(`i18nextLng`));
  // The date format coming from the BE should always
  // be in this format (InvariantCulture)
  const parsedBirthday = moment(profile.birthday, `MM/DD/YYYY`).format(`L`);
  return {
    type: SET_PROFILE,
    payload: { ...profile, birthday: parsedBirthday }
  };
};

export const setTokens = (token, refreshToken, role, username) => ({
  type: SET_TOKENS,
  payload: {
    refreshToken,
    role,
    token,
    username
  }
});

export const setClaims = (claims) => {
  localStorage.setItem(CLAIMS, JSON.stringify(claims));
  return {
    type: SET_CLAIMS,
    payload: claims
  };
};

export const fetchProfile = () => async (dispatch) => {
  try {
    dispatch(showSpinner());
    const profile = await dispatch(get(`/user`));
    const { data } = profile;

    localStorage.setItem(USERNAME_KEY, `${profile.data.firstName} ${profile.data.lastName}`);

    dispatch(setProfile(data));
    dispatch(setIsPasswordIntendedToChange(data.isPasswordIntendedToChange));
    dispatch(setIsProfileIntendedToApprove(data.isProfileIntendedToApprove));
    dispatch(setIsProfileActive(data.isActive));
    dispatch(setIsOpenPayment(data.isOpenPayment));
    dispatch(hideSpinner());
  } catch (error) {
    let message;

    if (error.response && error.response.data && error.response.data.message) {
      switch (error.response.data.message) {
        case `users.get.error.candidate.notFound`:
          message = `modules.localization.register.authenticated.error.candidate.notFound`;
          break;

        default:
          message = `modules.localization.register.anonymous.smartNotifications.error.generic`;
          break;
      }
    }

    dispatch(
      setErrorNotification({
        messageTranslationKey: message,
        titleTranslationKey: `modules.localization.register.anonymous.smartNotifications.error.error`
      })
    );
    dispatch(hideSpinner());
  }
};

export const fetchClaims = () => async (dispatch) => {
  try {
    dispatch(showSpinner());
    const claims = await dispatch(get(`/admin/claims`));
    const { data } = claims;

    dispatch(setClaims(data.claims));
    dispatch(hideSpinner());
  } catch (error) {
    dispatch(
      setErrorNotification({
        messageTranslationKey: `modules.localization.register.anonymous.smartNotifications.error.generic`,
        titleTranslationKey: `modules.localization.register.anonymous.smartNotifications.error.error`
      })
    );
    dispatch(hideSpinner());
  }
};
